import { ContextType } from "react";
import { ApiContext, apiContextSubscriber, } from "./apiProvider";
import { ApiVersionName, ResultObj, allFetch, getControllerApiVersion } from "./Domain";

/** ApiManager 的架構 class
 * @function init 初始註冊adapter
 * @function checkDefaultHOF 檢查 API adapter 版本
 * @author Olar
 */
export class ApiManagerBase<T extends DefaultAdapterBase> {
    /** 相對應 adapter version */
    readonly apiManagerVersion: Record<ApiVersionName | 'defaultAdapter', T> = {} as Record<ApiVersionName | 'defaultAdapter', T>
    /** 目前正使用之 api adapter */
    adapter: T
    /** 目前正使用之 api version */
    version: ApiVersionName | undefined;

    constructor(version?: ApiVersionName) {
        this.adapter = this.apiManagerVersion.defaultAdapter;
        this.version = version;
    }

    /** 初始註冊 adapter 
     * @param controllerName 控制項 API 名稱
    */
    async init(): Promise<ApiManagerBase<T>> {
        let target_version: ApiVersionName | undefined = this.version;
        if (target_version === undefined) {
            // 沒有的話就嘗試取得api_version.json，取得不到就採用localstorage的預設API版本
            let getAPIversionResult = await getControllerApiVersion(this.constructor.name);
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }
        // 將對應 version 註冊進 adapter
        this.adapter = this.apiManagerVersion[target_version || 'defaultAdapter'] as T

        if (!this.adapter) {
            throw new Error(`遺失 ${this.constructor.name} adapter`);
        }
        return this;
    }

    /** 執行API前檢查 API adapter 版本 
     * @param controllerName 控制項 API 名稱
     * @param childFunction 實際api 執行功能
    */
    checkDefaultHOF = <obj extends any[], result = ResultObj>(childFunction: (...args: obj) => Promise<result>) =>
        async (...args: obj) => {
            // 尋找到對應adapter name
            const apiStr = Object.keys(this.apiManagerVersion)[Object.values(this.apiManagerVersion).findIndex(ele => ele === this.adapter)]
            // 執行 Default Alert 提醒開發人員是否已 run 過 init
            this.adapter.DefaultAlert(this.constructor.name, apiStr);
            // 執行並回傳原始函式
            return await childFunction.apply(this, args);
        }

    /** 取消 pending 中請求 */
    cancelRequest = this.checkDefaultHOF(async () => this.adapter.cancelRequest());

    /** 存取暫存空間資料 
     * @param apiName — example: "CarCreate"
     * @param data — 有給則為儲存, 沒給則為取得 
     * @returns ResultObj */
    handleTemporaryStorageOperation = this.checkDefaultHOF(async (apiName: string, data?: any) => this.adapter.handleTemporaryStorageOperation(apiName, data));

}

/** 預設 api adapter 的架構 */
export class DefaultAdapterBase {
    /** 保存 AbortController 實例*/
    abortController: AbortController | undefined = undefined;
    /** apiContext 實例*/
    private apiContext: ContextType<typeof ApiContext> = undefined
    /** 訂閱 context 變化*/
    private unsubscribe: (() => void) | null = null

    constructor() {
        //訂閱 context 變化
        this.unsubscribe = apiContextSubscriber.subscribe(context => {
            this.apiContext = context
        })
    }

    /** 存取暫存空間資料 
     * @param apiName — example: "CarCreate"
     * @param data — 有給則為儲存, 沒給則為取得 
     * @returns ResultObj */
    readonly handleTemporaryStorageOperation = async (apiName: string, data?: any) => {
        let result: ResultObj = {
            message: '',
            success: false,
            data: null
        }
        if (this.apiContext) {
            if (apiName) result = await this.apiContext?.handleTemporaryStorageOperation(apiName, data)
            else result.message = `遺失 apiName 請確定參數正確`
        }
        else result.message = `遺失 apiContext`
        return result
    }

    /** 預設警示
     * @param controllerName api 控制項名稱
     * @param adapterName 目前所使用 adapter 名稱 
     * @ 重要! 若未執行或遺失 API version, 設定值為預設 api 此功能會警示開發者 
     * @author Olar
     */
    readonly DefaultAlert = (controllerName: string, adapterName: string | ApiVersionName) => {
        if (Object.values(ApiVersionName).some(api => api === adapterName)) {
            return
        } else {
            console.error(`遺失 ${controllerName} Api，請先執行init()，初始化MealApiManager`)
        }
    }

    /** 註冊 AbortController 的 allFetch 方法 */
    readonly abortAllFetch = async <T = { [key: string]: any } | FormData>(uri?: string, obj?: T,) => {
        // 註冊 AbortController
        this.abortController = new AbortController();
        try {
            return await allFetch(uri, obj, undefined, this.abortController);
        } finally {
            // api 完成後解除註冊 AbortController
            this.abortController = undefined;
        }
    }

    /** 取消 pending 中請求 */
    readonly cancelRequest = () => {
        if (this.abortController) {
            // 中止 api 請求
            this.abortController.abort();
        } else {
            console.error('fetch 時未註冊 AbortController, 或 api 已完成')
        }
    }

    /** 比對真正有修改資料 
     * @param originData 原始資料
     * @param newData 新資料
     * @param didUpdateData 真正有更動之值, 請把必填
     * 欄位放入此資料
    */
    readonly compareDataChange = ({ originData, newData, didUpdateData }:
        { originData: any, newData: any, didUpdateData: any }
    ) => {
        if (originData && typeof originData === 'object') {
            if (newData && typeof newData === 'object') {
                // 比對 originData 與 newData 之中不同之值
                for (let keyIndex in Object.keys(newData)) {
                    let key = Object.keys(newData)[keyIndex]
                    // 若有不同再將其放入
                    if (newData[key] !== originData[key]) {
                        didUpdateData[key] = newData[key]
                    }
                }
                return didUpdateData
            } else {
                return newData
            }
        } else return newData
    }
}
